import { Box, Typography } from '@mui/material';

import { OPACITY } from '../../../theme';
import { GROWTH } from '../tokens';
import type { SchemeLevel } from '../types';
import { BandDot, CardHead, GROWTH_CARD_SX, SMALL_SX, STRONG_SX, bandNames } from './cardParts';

export type Move = 'up' | 'same' | 'down';

export function moveOf(boyBand: number, laterBand: number): Move {
  if (laterBand > boyBand) return 'up';
  if (laterBand < boyBand) return 'down';
  return 'same';
}

const KEY = [
  { move: 'up' as const, label: 'Moved up a band' },
  { move: 'same' as const, label: 'Same band' },
  { move: 'down' as const, label: 'Moved down a band' },
];

export function BandGrid({ grid, scheme, later, sub, open }: { grid: number[][]; scheme: SchemeLevel[]; later: string; sub: string; open: boolean }) {
  const names = bandNames(scheme);
  const rowTotals = grid.map((row) => row.reduce((a, b) => a + b, 0));
  const columnTotals = [0, 1, 2, 3, 4].map((j) => grid.reduce((a, row) => a + (row[j] ?? 0), 0));
  const total = rowTotals.reduce((a, b) => a + b, 0);
  const faded = { opacity: open ? OPACITY.provisional : 1 };
  return (
    <Box component="section" data-testid="band-grid" sx={GROWTH_CARD_SX}>
      <CardHead title={`Band at BOY and at ${later}`} sub={sub} open={open} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '160px 120px repeat(5, 88px)', gap: '4px', alignItems: 'center', fontVariantNumeric: 'tabular-nums' }}>
        <Box />
        <Box />
        <Typography component="span" sx={{ gridColumn: '3 / 8', textAlign: 'center', ...SMALL_SX, fontWeight: 700, color: GROWTH.ink, pb: '4px', borderBottom: `1px solid ${GROWTH.divider}` }}>
          {`Band at ${later}`}
        </Typography>
        <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 700, color: GROWTH.ink, pt: '4px', pb: '8px' }}>
          Band at BOY
        </Typography>
        <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 700, textAlign: 'center' }}>
          Students at BOY
        </Typography>
        {names.map((name) => (
          <Typography key={name} component="span" sx={{ ...SMALL_SX, fontWeight: 600, textAlign: 'center' }}>
            {name}
          </Typography>
        ))}
        {grid.map((row, i) => (
          <Box key={names[i] ?? i} sx={{ display: 'contents' }} data-testid="grid-row">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ...STRONG_SX }}>
              <BandDot scheme={scheme} band={i + 1} />
              {names[i]}
            </Box>
            <Typography component="span" data-testid="grid-row-total" sx={{ ...STRONG_SX, textAlign: 'center' }} style={faded}>
              {rowTotals[i]}
            </Typography>
            {row.map((value, j) => {
              const move = moveOf(i + 1, j + 1);
              return (
                <Box
                  key={`${i}-${j}`}
                  data-testid="grid-cell"
                  data-move={move}
                  sx={{ textAlign: 'center', py: '8px', borderRadius: '4px', bgcolor: GROWTH.move[move], fontFamily: GROWTH.font, fontSize: '16px', lineHeight: '24px', fontWeight: move === 'same' ? 700 : 500, color: value === 0 ? GROWTH.disabled : GROWTH.ink }}
                  style={faded}
                >
                  {value}
                </Box>
              );
            })}
          </Box>
        ))}
        <Box sx={{ gridColumn: '1 / -1', height: '1px', bgcolor: GROWTH.ink, mt: '4px' }} />
        <Typography component="span" sx={{ ...STRONG_SX, py: '4px' }}>
          {`Students at ${later}`}
        </Typography>
        <Typography component="span" data-testid="grid-total" sx={{ ...STRONG_SX, textAlign: 'center', py: '4px' }} style={faded}>
          {total}
        </Typography>
        {columnTotals.map((value, j) => (
          <Typography key={names[j] ?? j} component="span" data-testid="grid-column-total" sx={{ ...STRONG_SX, textAlign: 'center', py: '4px' }} style={faded}>
            {value}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: '20px', mt: '16px' }}>
        {KEY.map((entry) => (
          <Typography key={entry.move} component="span" sx={{ ...SMALL_SX, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <Box component="i" aria-hidden="true" sx={{ display: 'inline-block', width: 12, height: 12, borderRadius: '4px', bgcolor: GROWTH.move[entry.move], border: `1px solid ${GROWTH.moveBorder[entry.move]}` }} />
            {entry.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
